import { useEffect } from "react";

/**
 * Global, restrained micro-motion:
 * - Auto scroll-reveal for section headings, paragraphs, cards & media
 * - Gentle bob on the hero "scroll to enter" arrow
 * No layout shifts, no cursor trails, respects prefers-reduced-motion.
 */
export default function AlpineMicroMotion() {
  useEffect(() => {
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReduced) return;

    const revealSelectors = [
      "section h2",
      "section h3",
      "section p",
      "section figure",
      "section img",
      "section .eyebrow",
      "section blockquote",
      "[data-reveal]",
    ].join(",");

    const bobSelectors = ["#top .ri-arrow-down-line"];

    const tagged = new WeakSet<Element>();

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            // stagger a touch for elements entering together
            el.style.transitionDelay = `${Math.min(i * 60, 240)}ms`;
            el.classList.add("is-in");
            io.unobserve(el);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );

    const apply = () => {
      document.querySelectorAll<HTMLElement>(revealSelectors).forEach((el) => {
        if (tagged.has(el)) return;
        // skip elements inside the hero pinned wordmark to avoid conflicting with GSAP
        if (el.closest(".hero-pin")) return;
        tagged.add(el);
        el.classList.add("alpine-reveal");
        io.observe(el);
      });

      bobSelectors.forEach((sel) => {
        document.querySelectorAll<HTMLElement>(sel).forEach((el) => {
          el.classList.add("alpine-bob");
        });
      });
    };

    apply();
    const mo = new MutationObserver(() => apply());
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      io.disconnect();
    };
  }, []);

  return null;
}